import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  GridLegacy as Grid,
  LinearProgress,
  Chip,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField
} from '@mui/material';
import {
  Work as WorkIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  VideoCall as VideoCallIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HelpOutline as HelpOutlineIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';

// ✅ CORRECT import path for api
import { adminAPI } from '../../services/api';

const JobTrackingPipeline = ({ jobId, showOverview = false }) => {
  const [trackingData, setTrackingData] = useState(null);
  const [overviewData, setOverviewData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    page: 1,
    per_page: 50,
    priority: '',
    status: 'open',
    search: ''
  });

  useEffect(() => {
    fetchTrackingData();
  }, [jobId, showOverview, filters]);

  const fetchTrackingData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      if (showOverview) {
        const response = await adminAPI.getAllJobsTrackingOverview(filters);
        if (response.data.success) {
          setOverviewData(response.data.jobs_overview);
        }
      } else if (jobId) {
        const response = await adminAPI.getJobTrackingStats(jobId);
        if (response.data.success) {
          setTrackingData(response.data);
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const PipelineVisualization = ({ pipeline }) => {
    const stages = [
      {
        label: 'Applications Received',
        value: pipeline.total_applied,
        icon: <PeopleIcon />,
        color: 'info',
        details: `${pipeline.total_applied} candidates applied`
      },
      {
        label: 'CV Review',
        value: `${pipeline.cv_review.approved}/${pipeline.total_applied}`,
        icon: <AssignmentIcon />,
        color: 'warning',
        substages: [
          { label: 'Parsed', value: pipeline.cv_review.parsed },
          { label: 'Pending', value: pipeline.cv_review.pending_review },
          { label: 'Approved', value: pipeline.cv_review.approved },
          { label: 'Rejected', value: pipeline.cv_review.rejected }
        ]
      },
      {
        label: 'Interviews',
        value: `${pipeline.interviews.completed}/${pipeline.cv_review.approved}`,
        icon: <VideoCallIcon />,
        color: 'primary',
        substages: [
          { label: 'Scheduled', value: pipeline.interviews.scheduled },
          { label: 'Completed', value: pipeline.interviews.completed },
          { label: 'Pending', value: pipeline.interviews.pending }
        ]
      },
      {
        label: 'Final Decisions',
        value: `${pipeline.final_decisions.hired}/${pipeline.interviews.completed}`,
        icon: <CheckCircleIcon />,
        color: 'success',
        substages: [
          { label: 'Hired', value: pipeline.final_decisions.hired },
          { label: 'Rejected', value: pipeline.final_decisions.rejected },
          { label: 'Maybe', value: pipeline.final_decisions.maybe },
          { label: 'Pending', value: pipeline.final_decisions.pending_decision }
        ]
      }
    ];

    return (
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" fontWeight="bold">
              📦 Candidate Pipeline Journey
            </Typography>
            <Tooltip title="Refresh Data">
              <IconButton onClick={fetchTrackingData}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
          
          <Stepper orientation="vertical">
            {stages.map((stage, index) => (
              <Step key={index} active={true}>
                <StepLabel
                  StepIconComponent={() => (
                    <Avatar sx={{ bgcolor: `${stage.color}.main`, width: 40, height: 40 }}>
                      {stage.icon}
                    </Avatar>
                  )}
                >
                  <Typography variant="h6" fontWeight="bold">
                    {stage.label}
                  </Typography>
                  <Typography variant="h4" color={`${stage.color}.main`} fontWeight="bold">
                    {stage.value}
                  </Typography>
                </StepLabel>
                
                {stage.substages && (
                  <StepContent>
                    <Paper sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                      <Grid container spacing={2}>
                        {stage.substages.map((substage, subIndex) => (
                          <Grid item xs={3} key={subIndex}>
                            <Box textAlign="center">
                              <Typography variant="h5" fontWeight="bold" color={`${stage.color}.main`}>
                                {substage.value}
                              </Typography>
                              <Typography variant="caption">
                                {substage.label}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  </StepContent>
                )}
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>
    );
  };

  const JobOverviewCard = ({ job }) => (
    <Card sx={{ mb: 2, '&:hover': { boxShadow: 6, cursor: 'pointer' } }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {job.job_title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {job.job_code}
            </Typography>
          </Box>
          <Chip 
            label={job.priority} 
            color={getPriorityColor(job.priority)}
            size="small"
          />
        </Box>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={3}>
            <Box textAlign="center">
              <Typography variant="h5" fontWeight="bold" color="info.main">
                {job.total_candidates}
              </Typography>
              <Typography variant="caption">Applied</Typography>
            </Box>
          </Grid>
          
          <Grid item xs={3}>
            <Box textAlign="center">
              <Typography variant="h5" fontWeight="bold" color="warning.main">
                {job.cv_approved}
              </Typography>
              <Typography variant="caption">CV Approved</Typography>
            </Box>
          </Grid>
          
          <Grid item xs={3}>
            <Box textAlign="center">
              <Typography variant="h5" fontWeight="bold" color="primary.main">
                {job.interviews_completed}
              </Typography>
              <Typography variant="caption">Interviewed</Typography>
            </Box>
          </Grid>
          
          <Grid item xs={3}>
            <Box textAlign="center">
              <Typography variant="h5" fontWeight="bold" color="success.main">
                {job.hired}
              </Typography>
              <Typography variant="caption">Hired</Typography>
            </Box>
          </Grid>
        </Grid>
        
        <Box mt={2}>
          <LinearProgress 
            variant="determinate" 
            value={job.total_candidates > 0 ? (job.hired / job.total_candidates * 100) : 0}
            color="success"
            sx={{ height: 6, borderRadius: 3 }}
          />
          <Typography variant="caption" color="text.secondary">
            Overall Success Rate: {job.success_rate || 0}%
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) return <LinearProgress />;
  if (error) return <Alert severity="error">Error loading tracking data: {error}</Alert>;

  if (showOverview) {
    return (
      <Box>
        <Typography variant="h4" fontWeight="bold" mb={3}>
          <WorkIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
          📊 All Jobs Tracking Overview
        </Typography>
        
        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={filters.priority}
                  onChange={(e) => setFilters(prev => ({...prev, priority: e.target.value}))}
                >
                  <MenuItem value="">All Priorities</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({...prev, status: e.target.value}))}
                >
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="filled">Filled</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search jobs..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({...prev, search: e.target.value}))}
              />
            </Grid>
          </Grid>
        </Paper>

        {overviewData.map(job => (
          <JobOverviewCard key={job.job_id} job={job} />
        ))}
      </Box>
    );
  }

  if (!trackingData) return <Alert severity="info">Select a job to view tracking details</Alert>;

  const { job_details, candidate_pipeline, conversion_rates } = trackingData;

  return (
    <Box>
      {/* Job Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h4" fontWeight="bold">
                {job_details.title}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Job Code: {job_details.job_code}
              </Typography>
            </Box>
            <Chip 
              label={job_details.priority} 
              color={getPriorityColor(job_details.priority)}
              size="large"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Pipeline Visualization */}
      <PipelineVisualization pipeline={candidate_pipeline} />

      {/* Conversion Metrics */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" mb={3}>
            <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            📈 Conversion Metrics
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <Box textAlign="center">
                <Typography variant="h4" color="warning.main" fontWeight="bold">
                  {conversion_rates.cv_approval_rate}%
                </Typography>
                <Typography variant="body2">CV Approval Rate</Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={conversion_rates.cv_approval_rate} 
                  color="warning"
                  sx={{ mt: 1, height: 8, borderRadius: 4 }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={4}>
              <Box textAlign="center">
                <Typography variant="h4" color="primary.main" fontWeight="bold">
                  {conversion_rates.interview_completion_rate}%
                </Typography>
                <Typography variant="body2">Interview Completion Rate</Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={conversion_rates.interview_completion_rate} 
                  sx={{ mt: 1, height: 8, borderRadius: 4 }}
                />
              </Box>
            </Grid>
            
            <Grid item xs={4}>
              <Box textAlign="center">
                <Typography variant="h4" color="success.main" fontWeight="bold">
                  {conversion_rates.hire_rate}%
                </Typography>
                <Typography variant="body2">Hire Rate</Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={conversion_rates.hire_rate} 
                  color="success"
                  sx={{ mt: 1, height: 8, borderRadius: 4 }}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default JobTrackingPipeline;
